
import { supabase } from '@/integrations/supabase/client';
import { Student, Program } from '@/services/db/types';
import { toast } from 'sonner';

interface SupabaseStudent {
  id: number;
  name: string;
  surname?: string;
  fathers_name?: string;
  age?: number;
  address?: string;
  province?: string;
  country?: string;
  contact_number?: string;
  email?: string;
  profile_image?: string;
  qualification_level?: string;
  institution_address?: string;
  institution_phone?: string;
  institution_email?: string;
  authorized_by?: string;
  policy_note?: string;
  created_at?: string;
  updated_at?: string;
}

interface SupabaseProgram {
  id: string;
  student_id: number;
  category: string;
  title: string;
  fee: number;
  enrolled_at: string;
  duration: number;
  completion_date?: string;
  fee_status: 'paid' | 'pending' | 'partial';
  next_payment_date?: string;
  created_at?: string;
}

interface ExtendedProgram extends Program {
  id?: string;
}

class StudentSupabaseService {
  private isOnline = navigator.onLine;

  constructor() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncPendingChanges();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private convertToLocalStudent(supabaseStudent: SupabaseStudent, programs: SupabaseProgram[] = []): Student {
    return {
      id: supabaseStudent.id,
      name: supabaseStudent.name,
      surname: supabaseStudent.surname,
      fathersName: supabaseStudent.fathers_name,
      age: supabaseStudent.age,
      address: supabaseStudent.address,
      province: supabaseStudent.province,
      country: supabaseStudent.country,
      contactNumber: supabaseStudent.contact_number,
      email: supabaseStudent.email,
      profileImage: supabaseStudent.profile_image,
      qualificationLevel: supabaseStudent.qualification_level as any,
      institutionAddress: supabaseStudent.institution_address,
      institutionPhone: supabaseStudent.institution_phone,
      institutionEmail: supabaseStudent.institution_email,
      authorizedBy: supabaseStudent.authorized_by,
      policyNote: supabaseStudent.policy_note,
      programs: programs.map(p => ({
        id: p.id,
        category: p.category as any,
        title: p.title,
        fee: p.fee,
        enrolledAt: new Date(p.enrolled_at),
        duration: p.duration,
        completionDate: p.completion_date ? new Date(p.completion_date) : new Date(Date.now() + p.duration * 24 * 60 * 60 * 1000),
        feeStatus: p.fee_status as any,
        nextPaymentDate: p.next_payment_date ? new Date(p.next_payment_date) : undefined
      })),
      documents: [],
      createdAt: supabaseStudent.created_at || new Date().toISOString(),
      updatedAt: supabaseStudent.updated_at || new Date().toISOString()
    };
  }

  private convertToSupabaseStudent(student: Student) {
    return {
      id: student.id,
      name: student.name,
      surname: student.surname,
      fathers_name: student.fathersName,
      age: student.age,
      address: student.address,
      province: student.province,
      country: student.country,
      contact_number: student.contactNumber,
      email: student.email,
      profile_image: student.profileImage,
      qualification_level: student.qualificationLevel,
      institution_address: student.institutionAddress,
      institution_phone: student.institutionPhone,
      institution_email: student.institutionEmail,
      authorized_by: student.authorizedBy,
      policy_note: student.policyNote,
      created_at: typeof student.createdAt === 'string' ? student.createdAt : student.createdAt?.toISOString(),
      updated_at: typeof student.updatedAt === 'string' ? student.updatedAt : student.updatedAt?.toISOString()
    };
  }

  async syncAllStudents(): Promise<Student[]> {
    if (!this.isOnline) {
      return this.getLocalStudents();
    }

    try {
      // Fetch students from Supabase
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('*');

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
        return this.getLocalStudents();
      }

      // Fetch programs for all students
      const { data: programs, error: programsError } = await supabase
        .from('student_programs')
        .select('*');

      if (programsError) {
        console.error('Error fetching programs:', programsError);
      }

      // Convert and group data
      const convertedStudents: Student[] = (students || []).map(student => {
        const studentPrograms = (programs || []).filter(p => p.student_id === student.id);
        return this.convertToLocalStudent(student, studentPrograms);
      });

      // Update localStorage with synced data
      if (convertedStudents.length > 0) {
        localStorage.setItem('nalc_students', JSON.stringify(convertedStudents));
      }

      return convertedStudents;
    } catch (error) {
      console.error('Sync error:', error);
      return this.getLocalStudents();
    }
  }

  private getLocalStudents(): Student[] {
    try {
      const stored = localStorage.getItem('nalc_students');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading local students:', error);
      return [];
    }
  }

  async saveStudent(student: Student): Promise<Student> {
    // Always save to localStorage first
    const localStudents = this.getLocalStudents();
    const existingIndex = localStudents.findIndex(s => s.id === student.id);
    
    if (existingIndex >= 0) {
      localStudents[existingIndex] = student;
    } else {
      localStudents.push(student);
    }
    
    localStorage.setItem('nalc_students', JSON.stringify(localStudents));

    // Background sync to Supabase if online
    if (this.isOnline) {
      this.backgroundSyncStudent(student);
    } else {
      this.queueForSync(student);
    }

    return student;
  }

  private async backgroundSyncStudent(student: Student): Promise<void> {
    try {
      const supabaseStudent = this.convertToSupabaseStudent(student);
      
      // Upsert student
      const { error: studentError } = await supabase
        .from('students')
        .upsert(supabaseStudent);

      if (studentError) {
        console.error('Error syncing student:', studentError);
        this.queueForSync(student);
        return;
      }

      // Sync programs - extend student programs with ids if missing
      const extendedPrograms: ExtendedProgram[] = student.programs.map(program => ({
        ...program,
        id: (program as any).id || crypto.randomUUID()
      }));

      for (const program of extendedPrograms) {
        const programId = program.id!;
        const enrolledAt = program.enrolledAt instanceof Date ? program.enrolledAt.toISOString() : program.enrolledAt;
        const completionDate = program.completionDate instanceof Date ? program.completionDate.toISOString() : program.completionDate;
        const nextPaymentDate = program.nextPaymentDate instanceof Date ? program.nextPaymentDate.toISOString() : program.nextPaymentDate;
        
        const { error: programError } = await supabase
          .from('student_programs')
          .upsert({
            id: programId,
            student_id: student.id,
            category: program.category,
            title: program.title,
            fee: program.fee,
            enrolled_at: enrolledAt,
            duration: program.duration,
            completion_date: completionDate,
            fee_status: program.feeStatus,
            next_payment_date: nextPaymentDate
          });

        if (programError) {
          console.error('Error syncing program:', programError);
        }
      }

      console.log('Student synced to Supabase:', student.id);
    } catch (error) {
      console.error('Background sync error:', error);
      this.queueForSync(student);
    }
  }

  private queueForSync(student: Student): void {
    const queue = JSON.parse(localStorage.getItem('supabase_sync_queue') || '[]');
    const existingIndex = queue.findIndex((q: any) => q.id === student.id);
    
    if (existingIndex >= 0) {
      queue[existingIndex] = student;
    } else {
      queue.push(student);
    }
    
    localStorage.setItem('supabase_sync_queue', JSON.stringify(queue));
  }

  private async syncPendingChanges(): Promise<void> {
    const queue = JSON.parse(localStorage.getItem('supabase_sync_queue') || '[]');
    
    if (queue.length === 0) return;

    console.log(`Syncing ${queue.length} pending changes...`);
    
    for (const student of queue) {
      await this.backgroundSyncStudent(student);
    }
    
    // Clear the queue after successful sync
    localStorage.removeItem('supabase_sync_queue');
    toast.success('All changes synced to cloud');
  }

  async deleteStudent(id: number): Promise<void> {
    // Remove from localStorage
    const localStudents = this.getLocalStudents();
    const filteredStudents = localStudents.filter(s => s.id !== id);
    localStorage.setItem('nalc_students', JSON.stringify(filteredStudents));

    // Background delete from Supabase
    if (this.isOnline) {
      try {
        const { error } = await supabase
          .from('students')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting from Supabase:', error);
        }
      } catch (error) {
        console.error('Delete sync error:', error);
      }
    }
  }

  getSyncStatus(): 'synced' | 'pending' | 'offline' {
    if (!this.isOnline) return 'offline';
    
    const queue = JSON.parse(localStorage.getItem('supabase_sync_queue') || '[]');
    return queue.length > 0 ? 'pending' : 'synced';
  }
}

export const studentSupabaseService = new StudentSupabaseService();
